import type {
  AccountReviewListing,
  Competitor,
  CompetitorSet,
  ReviewSourceConnection,
  ReviewSourceKey,
} from '@common/types';

import { PROPERTIES } from './group-scope.seed';

export const REVIEW_SOURCE_LABELS: Readonly<Record<ReviewSourceKey, string>> = {
  tripadvisor: 'Tripadvisor',
  google: 'Google',
  booking: 'Booking.com',
  expedia: 'Expedia',
  survey: 'GuestRevu Surveys',
};

export const REVIEW_SOURCE_ORDER: ReadonlyArray<ReviewSourceKey> = [
  'survey',
  'tripadvisor',
  'google',
  'booking',
  'expedia',
];

export const REVIEW_SOURCE_CONNECTIONS_SEED: ReadonlyArray<ReviewSourceConnection> = [
  {
    organizationId: 'sun-intl',
    sourceKey: 'survey',
    label: 'GuestRevu Surveys',
    status: 'connected',
    authKind: 'internal',
    lastSyncIso: '2026-08-31T08:00:00.000Z',
    nextSyncIso: '2026-08-31T09:00:00.000Z',
    syncEveryHours: 1,
    backfillDays: 0,
    reviewsLast30Days: 4182,
    errorMessage: null,
  },
  {
    organizationId: 'sun-intl',
    sourceKey: 'tripadvisor',
    label: 'Tripadvisor',
    status: 'connected',
    authKind: 'oauth',
    lastSyncIso: '2026-08-31T06:30:00.000Z',
    nextSyncIso: '2026-08-31T18:30:00.000Z',
    syncEveryHours: 12,
    backfillDays: 365,
    reviewsLast30Days: 1146,
    errorMessage: null,
  },
  {
    organizationId: 'sun-intl',
    sourceKey: 'google',
    label: 'Google Business Profile',
    status: 'connected',
    authKind: 'oauth',
    lastSyncIso: '2026-08-31T07:15:00.000Z',
    nextSyncIso: '2026-08-31T13:15:00.000Z',
    syncEveryHours: 6,
    backfillDays: 365,
    reviewsLast30Days: 2038,
    errorMessage: null,
  },
  {
    organizationId: 'sun-intl',
    sourceKey: 'booking',
    label: 'Booking.com',
    status: 'error',
    authKind: 'api-key',
    lastSyncIso: '2026-08-27T04:00:00.000Z',
    nextSyncIso: null,
    syncEveryHours: 24,
    backfillDays: 180,
    reviewsLast30Days: 611,
    errorMessage: 'Partner credentials rejected (401). Re-enter the Booking.com API key.',
  },
  {
    organizationId: 'sun-intl',
    sourceKey: 'expedia',
    label: 'Expedia Group',
    status: 'disconnected',
    authKind: 'api-key',
    lastSyncIso: null,
    nextSyncIso: null,
    syncEveryHours: 24,
    backfillDays: 180,
    reviewsLast30Days: 0,
    errorMessage: null,
  },
];

const LISTED_SOURCES: ReadonlyArray<ReviewSourceKey> = [
  'tripadvisor',
  'google',
  'booking',
  'expedia',
];

function buildListings(): Record<string, AccountReviewListing[]> {
  const map: Record<string, AccountReviewListing[]> = {};
  PROPERTIES.forEach((property, index) => {
    map[property.id] = LISTED_SOURCES.map((sourceKey, sourceIndex) => {
      const enabled = !(sourceKey === 'expedia' && index % 3 === 0);
      // One property per source deliberately carries a mismatched listing name so the
      // "this listing looks wrong" warning has something real to fire on.
      const mismatched = index % 7 === 3 && sourceKey === 'booking';
      return {
        accountId: property.id,
        sourceKey,
        listingUrl: `https://www.${sourceKey}.com/hotel/${property.id}`,
        listingId: `${sourceKey.slice(0, 2).toUpperCase()}-${100000 + index * 137 + sourceIndex}`,
        listingName: mismatched ? `${property.name} Annex` : property.name,
        isEnabled: enabled,
        verified: enabled && !mismatched,
        lastSyncIso: enabled ? '2026-08-31T06:30:00.000Z' : null,
        reviewsIngested: enabled ? 240 + ((index * 53 + sourceIndex * 29) % 900) : 0,
      };
    });
  });
  return map;
}

export const ACCOUNT_REVIEW_LISTINGS_SEED: Readonly<Record<string, AccountReviewListing[]>> =
  buildListings();

const COMPETITOR_POOL: ReadonlyArray<Omit<Competitor, 'id' | 'isPrimary'>> = [
  {
    name: 'The Westcliff',
    sourceKey: 'tripadvisor',
    listingUrl: 'https://www.tripadvisor.com/h/westcliff',
    market: 'Luxury city',
    starRating: 5,
    averageRating: 4.6,
  },
  {
    name: 'Radisson Blu Gautrain',
    sourceKey: 'google',
    listingUrl: 'https://www.google.com/maps/place/radisson',
    market: 'Upper upscale',
    starRating: 4,
    averageRating: 4.3,
  },
  {
    name: 'Hyatt Regency',
    sourceKey: 'booking',
    listingUrl: 'https://www.booking.com/hotel/hyatt',
    market: 'Upper upscale',
    starRating: 5,
    averageRating: 4.4,
  },
  {
    name: 'Protea Fire & Ice',
    sourceKey: 'tripadvisor',
    listingUrl: 'https://www.tripadvisor.com/h/fireandice',
    market: 'Upscale',
    starRating: 4,
    averageRating: 4.1,
  },
  {
    name: 'DaVinci on Nelson Mandela Square',
    sourceKey: 'google',
    listingUrl: 'https://www.google.com/maps/place/davinci',
    market: 'Luxury city',
    starRating: 5,
    averageRating: 4.2,
  },
];

function buildCompetitorSets(): Record<string, CompetitorSet> {
  const map: Record<string, CompetitorSet> = {};
  PROPERTIES.forEach((property, index) => {
    const size = 3 + (index % 3);
    map[property.id] = {
      accountId: property.id,
      market: `${property.location} upper upscale`,
      benchmarkGroup: `${property.region} · 4-5 star`,
      competitors: COMPETITOR_POOL.slice(0, size).map((competitor, i) => ({
        ...competitor,
        id: `${property.id}-comp-${i + 1}`,
        isPrimary: i === 0,
      })),
    };
  });
  return map;
}

export const COMPETITOR_SETS_SEED: Readonly<Record<string, CompetitorSet>> = buildCompetitorSets();
